// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Link,
  Tag,
} from '@carbon/react';
import CoverageStatusPill from './CoverageStatusPill';
import type { ChecklistItem, DocumentLink } from '../../../types';

interface ChecklistTableProps {
  items: ChecklistItem[];
  /** Session documents, used to resolve satisfied_by ids to filenames. */
  documents: DocumentLink[];
  provisionalEquipment: Record<string, unknown>[];
  onBypass: (_item: ChecklistItem) => void;
  onRemoveBypass: (_item: ChecklistItem) => void;
  onReviewMatch: (_item: ChecklistItem) => void;
}

interface ChecklistGroup {
  key: string;
  label: string;
  items: ChecklistItem[];
}

/** Group items under "Site documents" or per-equipment headers like
 * "Generator · Caterpillar C32 · 3 units (GEN-01, GEN-02, GEN-03)". */
function groupItems(items: ChecklistItem[]): ChecklistGroup[] {
  const groups = new Map<string, ChecklistGroup>();
  for (const item of items) {
    const key = item.equipment_group ?? 'site';
    let group = groups.get(key);
    if (!group) {
      group = { key, label: groupLabel(item), items: [] };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  // Site documents always lead, then equipment groups in arrival order.
  return Array.from(groups.values()).sort((a, b) =>
    a.key === 'site' ? -1 : b.key === 'site' ? 1 : 0
  );
}

function groupLabel(item: ChecklistItem): string {
  if (!item.equipment_group) return 'Site documents';
  const parts: string[] = [];
  if (item.component_type) parts.push(titleCase(item.component_type));
  const makeModel = [item.manufacturer, item.model].filter(Boolean).join(' ');
  if (makeModel) parts.push(makeModel);
  if (item.equipment_ids.length > 0) {
    const ids = item.equipment_ids;
    const idText = ids.length > 3 ? `${ids[0]} to ${ids[ids.length - 1]}` : ids.join(', ');
    parts.push(`${ids.length} unit${ids.length === 1 ? '' : 's'} (${idText})`);
  }
  return parts.join(' · ') || item.equipment_group;
}

function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function matchedFileText(item: ChecklistItem, documentsById: Map<string, DocumentLink>): string {
  if (item.status === 'bypassed') {
    return [item.bypass_reason, item.bypass_actor].filter(Boolean).join(' · ') || 'Bypassed';
  }
  if (item.satisfied_by.length === 0) return 'Waiting for document';
  return item.satisfied_by
    .map(id => {
      const doc = documentsById.get(id);
      if (!doc) return id;
      const confidence =
        typeof doc.confidence === 'number'
          ? ` · ${Math.round(doc.confidence * 100)}% confidence`
          : '';
      return `${doc.filename ?? doc.document_id}${confidence}`;
    })
    .join(', ');
}

const HEADERS = ['Document', 'Requirement', 'Status', 'Matched file', 'Action'];

/**
 * The document checklist per the design mock: one table, full-width group
 * header rows, columns Document | Requirement | Status | Matched file |
 * Action. Rows render purely from polled server state — no local status
 * flips (a bypassed item can reopen server-side when a matching document
 * arrives).
 */
export default function ChecklistTable({
  items,
  documents,
  provisionalEquipment,
  onBypass,
  onRemoveBypass,
  onReviewMatch,
}: ChecklistTableProps) {
  const groups = useMemo(() => groupItems(items), [items]);
  const documentsById = useMemo(
    () => new Map(documents.map(doc => [doc.document_id, doc])),
    [documents]
  );

  return (
    <Table size="md" className="obw-checklist-table" aria-label="Document checklist">
      <TableHead>
        <TableRow>
          {HEADERS.map(header => (
            <TableHeader key={header}>{header}</TableHeader>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {groups.map(group => (
          <React.Fragment key={group.key}>
            <TableRow className="obw-group-row">
              <TableCell colSpan={HEADERS.length}>{group.label}</TableCell>
            </TableRow>
            {group.items.map(item => (
              <TableRow key={item.item_id}>
                <TableCell>{item.expected_doc_type}</TableCell>
                <TableCell>
                  {item.requirement === 'required' ? 'Required' : 'Recommended'}
                </TableCell>
                <TableCell>
                  <CoverageStatusPill status={item.status} />
                </TableCell>
                <TableCell className="obw-matched-file">
                  {matchedFileText(item, documentsById)}
                </TableCell>
                <TableCell>
                  {item.status === 'bypassed' ? (
                    <Link onClick={() => onRemoveBypass(item)}>Remove bypass</Link>
                  ) : item.status === 'needs_review' ? (
                    // A weak match takes a decision either way: inspect it or
                    // bypass it. Without the bypass affordance here, a required
                    // needs-review row blocks submit with no available action.
                    <>
                      <Link onClick={() => onReviewMatch(item)}>Review match</Link>
                      <span className="obw-action-sep" aria-hidden="true">
                        {' · '}
                      </span>
                      <Link onClick={() => onBypass(item)}>Bypass</Link>
                    </>
                  ) : item.status === 'expected' ? (
                    <Link onClick={() => onBypass(item)}>Bypass</Link>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </React.Fragment>
        ))}
        {provisionalEquipment.length > 0 && (
          <>
            <TableRow className="obw-group-row">
              <TableCell colSpan={HEADERS.length}>
                Provisional equipment{' '}
                <Tag type="outline" size="sm">
                  Provisional
                </Tag>
              </TableCell>
            </TableRow>
            {provisionalEquipment.map((equipment, index) => (
              <TableRow key={String(equipment.equipment_id ?? index)}>
                <TableCell colSpan={HEADERS.length} className="obw-provisional-row">
                  {String(equipment.equipment_id ?? 'unknown')} — proposed from document tags;
                  checklist items appear once equipment ingest confirms it.
                </TableCell>
              </TableRow>
            ))}
          </>
        )}
      </TableBody>
    </Table>
  );
}
